import { usePageData } from '@components/context';
import axios, { CancelToken } from 'axios';
import { useState } from 'react';

/**
 * Custom hook for performing a WordPress AJAX request.
 *
 * @param {string} action - The WordPress action name to be included in the AJAX request.
 *
 * @return {[
 *   function(Object|FormData): Promise<import('axios').AxiosResponse<any>>, // The function to perform the AJAX request.
 *   function(string=): void // The function to cancel the ongoing request with an optional cancel message.
 * ]}
 *    Returns an array containing:
 *      - The function to initiate the AJAX request. Accepts either a plain object or FormData as the payload.
 *      - A function to cancel the AJAX request in progress, with an optional cancel message.
 *
 * @throws Will throw an error if `ajaxurl` is missing from `generalData`.
 */

const useAjax = action => {
  const [source, setSource] = useState();
  const {
    generalData: { ajaxurl, nonces },
  } = usePageData();

  if (!ajaxurl) {
    throw Error(
      'The `ajaxurl` is missing from the generalData. Please pass it from the page template to the window object.',
    );
  }

  return [
    data => {
      const cancelTokenSource = CancelToken.source();
      setSource(cancelTokenSource);
      let postData;

      if (data instanceof FormData) {
        postData = data;
        postData.append('action', action);

        if (nonces && nonces[action]) {
          postData.append('_ajax_nonce', nonces[action]);
        }
      } else {
        postData = {
          ...data,
          action,
          _ajax_nonce: nonces && nonces[action] ? nonces[action] : undefined,
        };
      }

      return axios.post(ajaxurl, postData, {
        cancelToken: cancelTokenSource.token,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    message => {
      source.cancel(message);
    },
  ];
};

export default useAjax;
